import type { ReactNode } from 'react'
import {
  ActivitySquare,
  Bike,
  Clock,
  Disc3,
  Dumbbell,
  Flower2,
  Footprints,
  Gauge,
  HeartPulse,
  Layers3,
  Mic2,
  Moon,
  Music2,
  PersonStanding,
  PlayCircle,
  Radio,
  Users2,
  Wind,
  Zap,
  type LucideIcon,
} from 'lucide-react'
import { motion } from 'motion/react'
import type { Discipline, DisciplineStats } from '@/hooks/useDashboardChannel'
import { workoutNouns } from '@/lib/discipline'
import { formatPrecise } from '@/lib/format'
import { CountUp } from '@/components/dashboard/fx/CountUp'
import { Reveal } from '@/components/dashboard/fx/Reveal'
import { SpotlightCard } from '@/components/dashboard/fx/SpotlightCard'
import { EASE } from '@/components/dashboard/fx/motion'

const TOTALS_ICON: Record<Discipline, LucideIcon> = {
  all: ActivitySquare,
  cycling: Bike,
  running: PersonStanding,
  walking: Footprints,
  pilates: Layers3,
  yoga: Flower2,
  stretching: Wind,
  meditation: Moon,
  cardio: HeartPulse,
  strength: Dumbbell,
}

const NUMBER_SIZE = {
  xl: 'text-[clamp(4.5rem,11vw,9rem)]',
  lg: 'text-[clamp(2.1rem,5vw,4.5rem)]',
  md: 'text-[clamp(1.9rem,3.6vw,3.25rem)]',
}

interface TileProps {
  className?: string
  delay?: number
  icon: LucideIcon
  label: string
  value?: number
  decimals?: number
  suffix?: string
  size?: keyof typeof NUMBER_SIZE
  note?: ReactNode
  children?: ReactNode
}

export function Tile({ className = '', delay = 0, icon: Icon, label, value, decimals, suffix, size = 'md', note, children }: TileProps) {
  return (
    <Reveal className={className} delay={delay} y={28}>
      <SpotlightCard className="relative flex h-full min-h-[10.5rem] flex-col justify-between overflow-hidden p-5 sm:p-6">
        <Icon
          aria-hidden
          strokeWidth={1}
          className={`pointer-events-none absolute -right-4 -top-4 text-foreground/[0.05] ${size === 'xl' ? 'size-72' : 'size-32'}`}
        />
        <span className="flex size-9 items-center justify-center rounded-full bg-primary/10 text-primary">
          <Icon className="size-4" />
        </span>
        <div className="flex flex-col gap-2">
          {children ?? (
            <CountUp
              value={value ?? 0}
              decimals={decimals}
              suffix={suffix}
              className={`font-display font-semibold leading-[0.9] tracking-tighter ${NUMBER_SIZE[size]}`}
            />
          )}
          <span className="text-sm text-muted-foreground">{label}</span>
          {note && <span className="text-xs text-muted-foreground/80">{note}</span>}
        </div>
      </SpotlightCard>
    </Reveal>
  )
}

function ProgressRing({ percent }: { percent: number }) {
  const r = 42
  return (
    <div className="relative size-24">
      <svg viewBox="0 0 100 100" className="size-full -rotate-90">
        <circle cx="50" cy="50" r={r} fill="none" strokeWidth="7" className="stroke-muted" />
        <motion.circle
          cx="50"
          cy="50"
          r={r}
          fill="none"
          strokeWidth="7"
          strokeLinecap="round"
          className="stroke-primary"
          initial={{ pathLength: 0 }}
          whileInView={{ pathLength: percent / 100 }}
          viewport={{ once: true }}
          transition={{ duration: 1.8, ease: EASE }}
        />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center font-display text-xl font-semibold tabular-nums">
        {percent}%
      </span>
    </div>
  )
}

interface StatBentoProps {
  stats: DisciplineStats
  discipline: Discipline
}

export function StatBento({ stats, discipline }: StatBentoProps) {
  const { singular, plural, pluralCapitalized } = workoutNouns(discipline)
  const t = stats.totals
  const years = t.total_ride_hours / 8766
  const p = stats.scrape_progress

  return (
    <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
      <Tile
        className="col-span-2 lg:row-span-2"
        icon={TOTALS_ICON[discipline]}
        label={`${pluralCapitalized} catalogued`}
        value={t.rides}
        size="xl"
        note={`${formatPrecise(p.rides_detailed)} with a fully synced playlist`}
      />
      <Tile delay={0.05} icon={Music2} label="Unique songs" value={t.songs} size="lg" />
      <Tile delay={0.1} icon={Mic2} label="Unique artists" value={t.artists} size="lg" />
      <Tile delay={0.05} icon={Disc3} label="Unique albums" value={t.albums} size="lg" />
      <Tile delay={0.1} icon={Users2} label="Instructors tracked" value={t.instructors} size="lg" />

      <Tile
        className="col-span-2"
        icon={Clock}
        label="Hours of music catalogued"
        value={t.total_ride_hours}
        size="lg"
        note={`≈ ${years.toFixed(1)} years of nonstop music`}
      />
      <Tile delay={0.05} icon={Zap} label="One-hit-wonder artists" value={t.one_hit_artists} />
      <Tile delay={0.1} icon={Gauge} label={`Avg. songs per ${singular}`} value={t.avg_songs_per_ride} decimals={1} />

      <Tile className="col-span-2" icon={PlayCircle} label="Total song-plays" value={t.total_song_plays} size="lg" />
      <Tile delay={0.05} icon={Radio} label="Explicit tracks" value={t.explicit_percent} decimals={0} suffix="%" />
      <Tile delay={0.1} icon={Radio} label={`Synced ${plural}`}>
        <ProgressRing percent={p.percent} />
      </Tile>
    </div>
  )
}
